import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Order, OrderStatus, fetchOrders, fetchOrderById, updateOrder, OrderDetail } from '../../services/order';

// --- State Interfaces ---
interface OrderFilters {
    status: OrderStatus | 'all';
    lender: string;
    dateRange: { from?: string; to?: string };
    brand?: string;
}

interface OrdersState {
    orders: Order[];
    selectedOrder: OrderDetail | null;
    total: number;
    loading: boolean;
    page: number;
    pageSize: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    searchQuery: string;
    filters: OrderFilters;
    error?: string;
    hasFetched: boolean;

}

// --- Initial State ---
const initialState: OrdersState = {
    orders: [],
    selectedOrder: null,
    total: 0,
    loading: false,
    page: 1,
    pageSize: 10,
    sortBy: 'orderCreatedAt',
    sortOrder: 'desc',
    searchQuery: '',
    filters: { status: 'all', lender: '', dateRange: {} },
    hasFetched: false
};

const normalizePopulatedId = <T>(value?: any): T | undefined => {
    if (!value || typeof value !== 'object') return undefined;
    return typeof value._id === 'object' ? value._id : value;
};


// --- Async Thunks ---
export const fetchOrdersThunk = createAsyncThunk(
    'orders/fetchOrders',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { orders: OrdersState };
            const { page, pageSize, sortBy, sortOrder, searchQuery, filters } = state.orders;

            const response = await fetchOrders({
                page,
                pageSize,
                sortBy,
                sortOrder,
                search: searchQuery,
                filters: {
                    status: filters.status,
                    lender: filters.lender,
                    dateRange: filters.dateRange,
                    brand: filters.brand, 
                },
            });

            return response;
        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    }
);

export const fetchOrderDetailThunk = createAsyncThunk<
    OrderDetail,
    string,
    { rejectValue: string }
>(
    'orders/fetchOrderDetail',
    async (orderId, { rejectWithValue }) => {
        try {
            const order: Order = await fetchOrderById(orderId, null);

            const orderDetail: OrderDetail = {
                ...order,
                merchantId: normalizePopulatedId(order.merchantId),
                customerId: normalizePopulatedId(order.customerId),
                partnerId: normalizePopulatedId(order.partnerId),

                variationString: '',
                documents: [],
                product: undefined,
                customer: undefined,
                merchant: undefined,
                partner: undefined,
                payment: undefined
            };

            return orderDetail;
        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    }
);

export const updateOrderThunk = createAsyncThunk(
    'orders/updateOrder',
    async ({ orderId, payload }: { orderId: string; payload: Partial<Order> }, { rejectWithValue }) => {
        try {
            const success = await updateOrder(orderId, payload, null);
            if (!success) throw new Error('Failed to update order');
            return { orderId, payload };
        } catch (err: any) {
            return rejectWithValue(err.message);
        }
    }
);

// --- Slice ---
const ordersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        setPage: (state, action: PayloadAction<number>) => {
            state.page = action.payload;
        },
        setPageSize: (state, action: PayloadAction<number>) => {
            state.pageSize = action.payload;
        },
        setSortBy: (state, action: PayloadAction<string>) => {
            state.sortBy = action.payload;
        },
        setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
            state.sortOrder = action.payload;
        },
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
        },
        setFilters: (state, action: PayloadAction<OrderFilters>) => {
            state.filters = action.payload;
        },
        resetFilters: (state) => {
            state.filters = { status: 'all', lender: '', dateRange: {} };
        },
        clearSelectedOrder: (state) => {
            state.selectedOrder = null;
        },
        setHasFetched: (state, action: PayloadAction<boolean>) => {
            state.hasFetched = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrdersThunk.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchOrdersThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.orders = action.payload.orders;
                state.total = action.payload.totalMatch;
                state.hasFetched = true;
            })
            .addCase(fetchOrdersThunk.rejected, (state, action) => {
                state.loading = false;
                state.orders = [];
                state.error = action.payload as string;
            })
            .addCase(fetchOrderDetailThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchOrderDetailThunk.fulfilled, (state, action) => {
                state.loading = false;
                state.selectedOrder = action.payload;
            })
            .addCase(fetchOrderDetailThunk.rejected, (state) => {
                state.loading = false;
                state.selectedOrder = null;
            })
            .addCase(updateOrderThunk.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateOrderThunk.fulfilled, (state, action) => {
                state.loading = false;

                const index = state.orders.findIndex(o => o.orderId === action.payload.orderId);
                if (index > -1) {
                    state.orders[index] = { ...state.orders[index], ...action.payload.payload };
                }

                if (state.selectedOrder?.orderId === action.payload.orderId) {
                    const { merchantId, customerId, partnerId, ...rest } = action.payload.payload;

                    state.selectedOrder = {
                        ...state.selectedOrder,
                        ...rest,
                        merchantId:
                            typeof merchantId === 'string'
                                ? { _id: merchantId, name: '', mobileNo: '', email: '', kycBank: { accountNo: "", ifsc: "", holderName: "" } }
                                : merchantId ?? state.selectedOrder.merchantId,
                        customerId:
                            typeof customerId === 'string'
                                ? { _id: customerId, name: '', mobileNo: '' }
                                : customerId ?? state.selectedOrder.customerId,
                        partnerId:
                            typeof partnerId === 'string'
                                ? { _id: partnerId, name: '', mobileNo: '', email: '', isEv: false }
                                : partnerId ?? state.selectedOrder.partnerId,
                    };
                }
            })
            .addCase(updateOrderThunk.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const {
    setPage,
    setPageSize,
    setSortBy,
    setSortOrder,
    setSearchQuery,
    setFilters,
    resetFilters,
    clearSelectedOrder,
    setHasFetched
} = ordersSlice.actions;

export default ordersSlice.reducer;
